import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { workflowService } from "@/services/breeze/WorkflowService";
import { Workflow } from "@/types/breeze/Workflow";
import { WorkflowEditorProvider } from "@/components/workflow/editor/WorkflowEditor";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import ErrorScreen from "@/components/ui/ErrorCard";

const WorkflowPage = () => {
  const params = useParams();
  const queryClient = useQueryClient();
  const workflowId = parseInt(params.workflowId as string);

  const {
    data: workflow,
    isLoading,
    error,
  } = useQuery<Workflow>({
    queryKey: ["workflow", workflowId],
    queryFn: () => workflowService.getWorkflow(workflowId),
  });

  const enableWorkflow = async () => {
    try {
      await workflowService.enableWorkflow(workflowId);
      queryClient.invalidateQueries({ queryKey: ["workflow", workflowId] });
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
    } catch (error) {
      console.error("Failed to enable workflow:", error);
    }
  };

  const disableWorkflow = async () => {
    try {
      await workflowService.disableWorkflow(workflowId);
      queryClient.invalidateQueries({ queryKey: ["workflow", workflowId] });
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
    } catch (error) {
      console.error("Failed to disable workflow:", error);
    }
  };

  const handleWorkflowUpdate = () => {
    queryClient.invalidateQueries({ queryKey: ["workflow", workflowId] });
    queryClient.invalidateQueries({ queryKey: ["workflows"] });
  };

  if (isLoading) {
    return <LoadingScreen fullPage={false} />;
  }

  if (error) {
    return <ErrorScreen error={error.message} />;
  }

  if (!workflow) {
    return <ErrorScreen error="Workflow not found" />;
  }

  return (
    <div className="h-[calc(100vh-64px)]">
      <WorkflowEditorProvider
        workflow={workflow}
        onWorkflowUpdate={handleWorkflowUpdate}
        onEnableWorkflow={enableWorkflow}
        onDisableWorkflow={disableWorkflow}
      />
    </div>
  );
};

export default WorkflowPage;
